import { Injectable } from '@angular/core';
import DateRange from '../models/date-range';
import {
  addDays,
  addHours,
  differenceInHours,
  differenceInDays,
  format,
  parse,
  parseISO,
  isEqual,
  addMonths,
  addYears,
  nextSaturday,
  nextSunday,
} from 'date-fns';

import { LogService } from './log.service';

// https://stackoverflow.com/questions/9772955/how-can-i-get-the-timezone-name-in-javascript
// TODO: Use https://github.com/dmfilipenko/timezones.json/blob/master/timezones.json

@Injectable({
  providedIn: 'root'
})
export class DateService {

  private readonly _monthNames =  ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];



  
  //console.log('parseDate: ' + date + ' === ' + zeroDate);

  private readonly _serviceName = 'DateService';

  public timezones = [
    {
      SysID: 'tz¬-7.00¬America_Denver',
      UUID: '58C289A8-D1C6-4CA3-B563-DC701327DBB8',
      MSSQLTimeZoneName: 'Mountain Standard Time',
      Name: 'Mountain Standard Time',
      TimezoneName: 'America/Denver',
      Zone: -7.0,
      UTCFormat: 'UTC-07:00',
    },
    {
      SysID: 'tz¬-6.00¬America_Chicago',
      UUID: '92303157-F009-4178-9387-A4BD57876EE6',
      MSSQLTimeZoneName: 'Central Standard Time',
      Name: 'Central Standard Time',
      TimezoneName: 'America/Chicago',
      Zone: -6.0,
      UTCFormat: 'UTC-06:00',
    },
    {
      SysID: 'tz¬-5.00¬America_New_York',
      UUID: 'A8AF211D-A12E-4211-8532-ACFEF82746F2',
      MSSQLTimeZoneName: 'US Eastern Standard Time',
      Name: 'US Eastern Standard Time',
      TimezoneName: 'America/New_York',
      Zone: -5.0,
      UTCFormat: 'UTC-05:00',
    },
    {
      SysID: 'tz¬-8.00¬America_Los_Angeles',
      UUID: 'CB3EA301-991E-4974-ABC4-E4D14C4B6C10',
      MSSQLTimeZoneName: 'Pacific Standard Time',
      Name: 'Pacific Standard Time',
      TimezoneName: 'America/Los_Angeles',
      Zone: -8.0,
      UTCFormat: 'UTC-08:00',
    },
  ];

  public months = [
    {
      abbreviation: 'Jan',
      name: 'January',
      id: 1,
      js: 0,
    },
    {
      abbreviation: 'Feb',
      name: 'February',
      id: 2,
      js: 1,
    },
    {
      abbreviation: 'Mar',
      name: 'March',
      id: 3,
      js: 2,
    },
    {
      abbreviation: 'Apr',
      name: 'April',
      id: 4,
      js: 3,
    },
    {
      abbreviation: 'May',
      name: 'May',
      id: 5,
      js: 4,
    },
    {
      abbreviation: 'Jun',
      name: 'June',
      id: 6,
      js: 5,
    },
    {
      abbreviation: 'Jul',
      name: 'July',
      id: 7,
      js: 6,
    },
    {
      abbreviation: 'Aug',
      name: 'August',
      id: 8,
      js: 7,
    },
    {
      abbreviation: 'Sep',
      name: 'September',
      id: 9,
      js: 8,
    },
    {
      abbreviation: 'Oct',
      name: 'October',
      id: 10,
      js: 9,
    },
    {
      abbreviation: 'Nov',
      name: 'November',
      id: 11,
      js: 10,
    },
    {
      abbreviation: 'Dec',
      name: 'December',
      id: 12,
      js: 11,
    },
  ];

  constructor(private log: LogService) {}

  /**
   * Gets the current DateTime in UTC, down to seconds.
   */
  public get nowUTC(): Date {
    var date = new Date();
    var now_utc = Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds()
    );

    return new Date(now_utc);
  }

  /**
   * Creates a new date using an existing date and treating the date components as UTC.   *
   * @param dt
   */
  public getAsUTCDate(dt: Date): Date {
    return new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate(), dt.getHours(), dt.getMinutes()));
  }

  /**
   * Gets the current DateTime in UTC, down to milliseconds.
   */
  public get nowUTCMilliseconds(): Date {
    var date = new Date();
    var now_utc = Date.UTC(
      date.getUTCFullYear(),
      date.getUTCMonth(),
      date.getUTCDate(),
      date.getUTCHours(),
      date.getUTCMinutes(),
      date.getUTCSeconds(),
      date.getUTCMilliseconds()
    );

    return new Date(now_utc);
  }

  /***
   * Gets the current UTC DateTime as a sortable string (e.g. 20191214T124751449Z)
   */
  public get nowUTCText(): string {
    var date = new Date();
    var nows = date.toISOString();
    return nows.split(':').join('').split('.').join('').split('-').join('');
  }

  /**
   * Creates a Date object with a time of 0:0:00 .
   * @param dateString  String version of the date.
   * @returns new Date object
   */
  public parseDate(dateString: string | Date): Date {
    if (dateString) {
      if (typeof dateString === 'string') {
        let date = this.parseDateFormat(dateString);

        const zeroDate = DateService.trimTime(date);
        return zeroDate;
      }
      return DateService.trimTime(dateString);
    }
    throw Error('null parameter "dateString"');
  }

  /**
   * Parses a string against an array of allowable Date formats
   * @param dateString
   */
  private parseDateFormat(dateString: string) {
    const diso = parseISO(dateString);
    if ((diso as any) != 'Invalid Date') {
      // console.log(`ParseISO value: ${dateString}.`);
      return diso;
    }

    let formats = ['yyyy-MM-dd', 'MM/dd/yyyy'];
    formats.forEach((f) => {
      const d = parse(dateString, f, null);
      if ((d as any) != 'Invalid Date') {
        // console.log(`Parse value: ${dateString} as a Date using format: ${f}.`);
        return d;
      }
    });

    throw Error(`Unable to parse value: ${dateString} as a Date.`);
  }

  /**
   * Creates a new Date object with a time of 0:0:00 .
   * @param date.
   * @returns new Date object
   */
  public static trimTime(date: Date): Date {
    if (date) {
      return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
    } else {
      throw Error('null parameter "date"');
    }
  }

  /**
   * Compares the UTC Day/Month/Year of 2 dates to determine equality
   * @param date1
   * @param date2
   */
  public isDateEqual(date1: Date, date2: Date): boolean {
    if (this.log.verboseLogging) {
      console.log(`${this._serviceName}.isDateEqual date1:`, date1);
      console.log(`${this._serviceName}.isDateEqual date2:`, date2);
    }

    const date1Day = date1.getUTCDate();
    const date1Month = date1.getUTCMonth();
    const date1Year = date1.getUTCFullYear();

    const date2Day = date2.getUTCDate();
    const date2Month = date2.getUTCMonth();
    const date2Year = date2.getUTCFullYear();

    if (this.log.verboseLogging) {
      console.log(`${this._serviceName}.isDateEqual date1 parts: day=${date1Day},month=${date1Month},year=${date1Year}`);
      console.log(`${this._serviceName}.isDateEqual date2 parts: day=${date2Day},month=${date2Month},year=${date2Year}`);
    }

    if (date1Day === date2Day && date1Month === date2Month && date1Year === date2Year) {
      if (this.log.verboseLogging) console.log('isDateEqual=true');
      return true;
    } else {
      if (this.log.verboseLogging) console.log('isDateEqual=false');
      return false;
    }
  }

  /**
   * Is this date today?
   * @param date
   */
  public isToday(date: Date): boolean {
    return this.isDateEqual(date, new Date());
  }

  /**
   * Is this date tomorrow?
   * @param date
   */
  public isTomorrow(date: Date) {
    return this.isDateEqual(date, this.addDays(new Date(), 1));
  }

  /**
   * Is this date the day after tomorrow?
   * @param date
   */
  public isOvermorrow(date: Date) {
    return this.isDateEqual(date, this.addDays(new Date(), 2));
  }

  /**
   *
   * @param startDate
   */
  public isFutureDate(date1: Date): boolean {
    const now = new Date();

    let d1 = this.makeValid(date1);

    d1 = new Date(d1.getUTCFullYear(), d1.getUTCMonth(), d1.getUTCDate());
    let d2 = new Date(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());

    d2.setDate(d2.getDate() + 1);

    return this.isDateGreaterThanOrEqualTo(d1, d2);
  }

  /**
   * Is date1 greater than date2?
   * @param date1
   * @param date2
   */
  public isDateGreaterThan(date1: Date, date2: Date): boolean {
    let d1 = this.makeValid(date1);
    let d2 = this.makeValid(date2);

    d1 = new Date(d1.getUTCFullYear(), d1.getUTCMonth(), d1.getUTCDate());
    d2 = new Date(d2.getUTCFullYear(), d2.getUTCMonth(), d2.getUTCDate());

    let val = d1 > d2;

    if (this.log.verboseLogging) console.log(`from './date.service'.isDateGreaterThan -  Is '${date1}' > '${date2}': ${val} `);

    return val;
  }

  /**
   * Is date1 greater than or equal to date2?
   * @param date1
   * @param date2
   */
  public isDateGreaterThanOrEqualTo(date1: Date, date2: Date): boolean {
    let d1 = this.makeValid(date1);
    let d2 = this.makeValid(date2);

    d1 = new Date(d1.getUTCFullYear(), d1.getUTCMonth(), d1.getUTCDate());

    d2 = new Date(d2.getUTCFullYear(), d2.getUTCMonth(), d2.getUTCDate());

    let val = d1 >= d2;

    return val;
  }

  /**
   * Is date1 less than date2?
   * @param date1
   * @param date2
   */
  public isDateLessThan(date1: Date, date2: Date): boolean {
    let d1 = this.makeValid(date1);
    let d2 = this.makeValid(date2);

    d1 = new Date(d1.getUTCFullYear(), d1.getUTCMonth(), d1.getUTCDate());
    d2 = new Date(d2.getUTCFullYear(), d2.getUTCMonth(), d2.getUTCDate());

    let val = d1 < d2;

    if (this.log.verboseLogging) console.log(`from './date.service'.isDateLessThan -  Is '${date1}' < '${date2}': ${val} `);

    return val;
  }

  /**
   *Gets the hours between 2 dates (this method wraps the date-fns differenceInHours function)
   * @param date1
   * @param date2
   */
  public getHoursBetween(date1: Date, date2: Date) {
    return differenceInHours(date1, date2);
  }

  /**
   * Gets the number of milliseconds between 2 dates.
   * @param date1
   * @param date2
   */
  public getMillisecondsBetween(date1: Date, date2: Date) {
    return date2.getTime() - date1.getTime();
  }

  public getDifferenceInDays(date1: Date, date2: Date) {
    return differenceInDays(date1, date2);
  }

  public daysBetween(date1: Date, date2: Date): Date[] {
    const days: Date[] = [];

    // Make a couple of variables to handle the swap (if needed)
    let d1 = new Date(+this.parseDate(date1));
    let d2 = new Date(+this.parseDate(date2));

    // first date is larger than the second, swap 'em
    if (this.isDateGreaterThanOrEqualTo(d1, d2)) {
      d1 = new Date(+this.parseDate(date2));
      d2 = new Date(+this.parseDate(date1));
    }

    days.push(d1);

    // if they are equal we are done!
    if (this.isDateEqual(d1, d2)) {
      return days;
    }

    let currentDate = d1;

    // And proceed up to the d2 value
    while (this.isDateLessThan(currentDate, d2)) {
      currentDate = addDays(currentDate, 1);
      days.push(currentDate);
    }

    if (this.log.verboseLogging) console.log(`daysBetween ${date1.toISOString()} and ${date2.toISOString()}`, days);

    return days;
  }

  public xdaysBetween(date1: Date, date2: Date): Date[] {
    const days: Date[] = [];

    // Make a couple of variables to handle the swap (if needed)
    let d1 = new Date(+this.parseDate(date1));
    let d2 = new Date(+this.parseDate(date2));

    // first date is larger than the second, swap 'em
    if (this.isDateGreaterThanOrEqualTo(d1, d2)) {
      d1 = new Date(+this.parseDate(date2));
      d2 = new Date(+this.parseDate(date1));
    }

    days.push(d1);

    // if they are equal we are done!
    if (this.isDateEqual(d1, d2)) {
      return days;
    }

    let currentDate = d1;

    // And proceed up to the d2 value
    while (this.isDateLessThan(currentDate, d2)) {
      currentDate = addDays(currentDate, 1);
      days.push(currentDate);
    }

    if (this.log.verboseLogging) console.log(`xdaysBetween ${date1.toISOString()} and ${date2.toISOString()}`, days);

    return days;
  }

  /**
   * Creates a new valid date from an invalid one
   */
  public makeValid(date: Date): Date {
    // console.log('from './date.service'.makeValid date=',date);

    if (date instanceof Date === false) {
      // console.warn('from './date.service'.makeValid date is not a valid instance of "Date"');
      let stringDate = new String(date);
      // console.log('from './date.service'.makeValid stringDate=',stringDate);

      let validDate = parseISO(stringDate.toString());
      // console.log('from './date.service'.makeValid validDate=',validDate);

      // console.log(`from './date.service'.makeValid - validDate instanceof Date=${validDate instanceof Date}`);

      if (validDate instanceof Date) {
        return validDate;
      }

      let errmsg = `Could not convert: ${date} to a Date.`;
      throw new Error(errmsg);
    } else {
      // console.log('from './date.service'.makeValid is already a valid instance of "Date"');
      return date;
    }
  }

  /**
   * Creates a new valid date from an invalid one
   * This is a copied and slightly modified version of makeValid()
   * I needed a version that would return null if null was passed in
   * but didn't want to change the existing version without understanding the ramifications
   * of everywhere that its called from
   */
  public makeNullableValid(date: Date | string): Date {
    // console.log('from './date.service'.makeValid date=',date);

    if (date === '') {
      return null;
    }
    if (date === null) {
      return null;
    }
    if (date instanceof Date === false) {
      // console.warn('from './date.service'.makeValid date is not a valid instance of "Date"');
      let stringDate = new String(date);
      // console.log('from './date.service'.makeValid stringDate=',stringDate);

      let validDate = parseISO(stringDate.toString());
      // console.log('from './date.service'.makeValid validDate=',validDate);

      // console.log(`from './date.service'.makeValid - validDate instanceof Date=${validDate instanceof Date}`);

      if (validDate instanceof Date) {
        // check for 0001-01-01 and replace with null
        if ((validDate as Date).getFullYear() === 1) {
          return null
        } else {
          return validDate;
        }
      }

      let errmsg = `Could not convert: ${date} to a Date.`;
      throw new Error(errmsg);
    } else {
      // console.log('from './date.service'.makeValid is already a valid instance of "Date"');
      if ((date as Date).getFullYear() === 1) {
        return null
      } else {
        return date as Date;
      }
      
    }
  }

  public makeValidUTCDate(date: Date): Date {
    
    if (date instanceof Date === false) {
      // console.warn('from './date.service'.makeValid date is not a valid instance of "Date"');
      let stringDate = new String(date);
      // console.log('from './date.service'.makeValid stringDate=',stringDate);

      let validDate = parseISO(stringDate.toString());
      // console.log('from './date.service'.makeValid validDate=',validDate);

      // console.log(`from './date.service'.makeValid - validDate instanceof Date=${validDate instanceof Date}`);

      if (validDate instanceof Date) {
        const newValue = this.convertLocalDateToUTCDate(validDate);
        return newValue;
      }

      let errmsg = `Could not convert: ${date} to a Date.`;
      throw new Error(errmsg);
    } else {
      // console.log('from './date.service'.makeValid is already a valid instance of "Date"');
      return date;
    }
  }

  /**
   * Converts a UTC date to local date
   */
  public convertUTCDateToLocalDate(utcDate: Date) {
    const date = this.makeValid(utcDate);

    return new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), date.getMinutes(), date.getSeconds()));
  }

  public convertLocalDateToUTCDate(localDate: Date) {
    return new Date(
      Date.UTC(
        localDate.getUTCFullYear(),
        localDate.getUTCMonth(),
        localDate.getUTCDate(),
        localDate.getUTCHours(),
        localDate.getUTCMinutes(),
        localDate.getUTCSeconds()
      )
    );
  }

  /**
   * getLocalTimeZoneName
   */
  public getLocalTimeZoneName() {}

  /**
   * Gets the name of the local timezone for display purposes
   */
  public get localTimeZone(): string {
    return Intl.DateTimeFormat().resolvedOptions().timeZone;
  }

  /**
   * Formats the date as a string: ("MM/dd/yyyy")
   * */
  public formatDate(date: Date) {
    const formatString = 'MM/dd/yyyy';

    return format(date, formatString);
  }

  /**
   * Formats the date as a string: ("YYYYMMDDHHmmss")
   * */
  public formatDateYYYYMMddHHmmss(date: Date, separateByDash: boolean) {
    if (separateByDash) {
      return format(date, 'yyyy-MM-dd-HH-mm-ss');
    }

    return format(date, 'yyyyMMddHHmmss');
  }

  /**
   * Formats the date as a string: ("yyyy-MM-dd")
   * */
  public formatDateYYYYMMDD(date: Date) {
    const formatString = 'yyyy-MM-dd';

    return format(date, formatString);
  }

  /**
   * Formats the date as a string: ("yyyy-MM-dd HH:mm"). Example: 2021-10-07 15:00
   *
   * */
  public formatDateTimeYYYYMMDDHmm(date: Date) {
    const formatString = 'yyyy-MM-dd HH:mm';

    return format(date, formatString);
  }

  /**
   * Formats a date time string.  Wraps date-fns format function
   * See https://date-fns.org/v2.21.1/docs/format
   *
   *
   * @param date
   * @param formatString
   * @returns
   */
  public formatDateTime(date: Date, formatString: string) {
    return format(date, formatString);
  }

  /**
   * Adds days to a date value
   * @param d
   * @param days
   */
  addDays(d: Date, days: number): Date {
    return new Date(d.getTime() + days * 1000 * 60 * 60 * 24);
  }

  /**
   * Adds hours to a Date
   * @param d
   * @param hours
   */
  public addHours(d: Date, hours: number): Date {
    return addHours(this.makeValid(d), hours);
  }

  /**
   * Gets a date that is seven days from now
   */
  sevenDaysFromNow(): Date {
    return this.addDays(new Date(), 7);
  }

  /**
   * Gets a date that is 30 days from now
   */
  thirtyDaysFromNow(): Date {
    return this.addDays(new Date(), 30);
  }

  /**
   * Gets a date "n" days from now
   * @param days
   * @returns
   */
  public getDaysFromNow(days: number): Date {
    return this.addDays(new Date(), days);
  }

  /**
   * Gets a date "n" days from now (UTC)
   * @param days
   * @returns
   */
  public getDaysFromNowUtc(days: number): Date {
    return this.addDays(this.nowUTC, days);
  }

  /**
   * Adds seconds to a date value
   * @param d
   * @param seconds
   */
  addSeconds(d: Date, seconds: number): Date {
    return new Date(d.getTime() + seconds * 1000);
  }

  /**
   * Adds milliseconds to a date value
   * @param d
   * @param milliseconds
   */
  addMilliSeconds(d: Date, milliseconds: number): Date {
    return new Date(d.getTime() + milliseconds);
  }

  /**
   * Returns a new date that is 'n' seconds from now
   * @param seconds
   */
  secondsFromNow(seconds: number) {
    return this.addSeconds(new Date(), seconds);
  }

  /**
   * Checks to see if a date is between 2 other dates (inclusive)
   * @param d
   * @param date1
   * @param date2
   */
  isBetween(d: Date, date1: Date, date2: Date): boolean {
    const validDate = this.makeValid(d);
    const validDate1 = this.makeValid(date1);
    const validDate2 = this.makeValid(date2);

    return this.isDateGreaterThanOrEqualTo(validDate, validDate1) && this.isDateLessThanOrEqualTo(validDate, validDate2);
  }

  /**
   * Is date less than or equal to?
   * @param date1
   * @param date2
   */
  public isDateLessThanOrEqualTo(date1: Date, date2: Date): boolean {
    let d1 = new Date(date1.getUTCFullYear(), date1.getUTCMonth(), date1.getUTCDate());

    let d2 = new Date(date2.getUTCFullYear(), date2.getUTCMonth(), date2.getUTCDate());

    let val = d1 <= d2;

    if (this.log.verboseLogging) console.log(`from './date.service'.isDateGreaterThan -  Is '${date1}' > '${date2}': ${val} `);

    return val;
  }

  /**
   * Gets the total number of days in a given month
   * @param date
   */
  public getDaysInMonth(date: Date): number {
    var year = date.getFullYear();
    var month = date.getMonth();
    const n = new Date(year, month, 0).getDate();

    console.log(`${this._serviceName}getDaysInMonth() = `, date, n);

    return n;
  }

  /**
   * Gets the number of days in a given month/year
   * @param m (1=12)
   * @param y
   * @returns
   */
  public getMonthDayCount(m, y) {
    // https://stackoverflow.com/a/27810609/386619
    return m === 2 ? (y & 3 || (!(y % 25) && y & 15) ? 28 : 29) : 30 + ((m + (m >> 3)) & 1);
  }

  /**
   * Checks to see if a date is in the past (UTC)
   * @param date
   */
  public isPast(date: Date) {
    return this.isDateLessThan(date, this.nowUTC);
  }

  /**
   * Compares a date with a nowutc + 7 days
   * @param numberofDays
   * @param date
   */
  public isLessThanDaysFromNow(numberofDays: number, date: Date): boolean {
    const d = this.makeValid(date);

    return this.isDateLessThanOrEqualTo(d, this.addDays(this.nowUTC, numberofDays));
  }

  /**
   * Gets a month number value for the current month (1-12)
   */
  public get currentMonth(): number {
    // add 1 because JS months are from 0 to 11.
    return new Date().getMonth() + 1;
  }

  public get currentYear(): number {
    // add 1 because JS months are from 0 to 11.
    return new Date().getFullYear();
  }
  /**
   * Compares 2 dates using date-fns (comparison includes time components)
   * @param date1
   * @param date2
   */
  public isDateTimeEqual(date1: Date, date2: Date) {
    const b = isEqual(this.makeValid(date1), this.makeValid(date2));

    return b;
  }

  /**
   * Converts an epoch date seconds value to a Javascript Date
   */
  public convertEpochToDate(seconds: number) {
    let d = new Date(0); // The 0 there is the key, which sets the date to the epoch
    d.setUTCSeconds(seconds);
    return d;
  }

  /**
   * * Converts an epoch date milliseconds value to a Javascript Date
   * @param milliseconds
   */
  public convertEpochMillisecondsToDate(milliseconds: number) {
    let d = new Date(0); // The 0 there is the key, which sets the date to the epoch
    d.setUTCSeconds(milliseconds / 1000);
    return d;
  }

  /**
   * Gets the date with time set to the end of the day (23:59:59)
   * @param date
   */
  public getEndOfDay(date: Date) {
    let d = this.makeValid(date);

    d.setHours(23, 59, 59);

    return d;
  }

  /**
   * Function that parses a datetime string that is known to be UTC into an actual Date that is UTC.
   * @param dateString
   */
  public parseUTCDateTime(dateString: string) {
    // console.log(`${this._serviceName}.parseUTCDateTime dateString=`,dateString);

    let date = parseISO(dateString);
    
    let utc = new Date(
      Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
      )
    );

    return utc;
  }

  /**
   * Creates a date as UTC from the date components of the provided date value
   * @param date
   */
  public toUTCDate(date: Date) {
    let utc = new Date(
      Date.UTC(
        date.getFullYear(),
        date.getMonth(),
        date.getDate(),
        date.getHours(),
        date.getMinutes(),
        date.getSeconds(),
        date.getMilliseconds()
      )
    );

    return utc;
  }

  /**
   * Adds a number of time intervals to the Date
   * @param date
   * @param interval
   * @param value
   */
  public addInterval(date: Date, interval: string, value: number): any {
    switch (interval) {
      case 'day':
        return this.addDays(date, value);
      case 'month':
        return this.addMonths(date, value);
      case 'year':
        return this.addYears(date, value);
      default:
        break;
    }
  }

  /**
   * Adds years to a date (wraps date-fns addYears)
   */
  public addYears(date: Date, years: number): any {
    return addYears(date, years);
  }

  /**
   * Adds months to a date (wraps date-fns addMonths)
   */
  public addMonths(date: Date, months: number): any {
    return addMonths(date, months);
  }

  /**
   * Creates a new date down to the hour component. Minutes, Seconds, Milleseconds are set to 0
   * @param date
   * @returns
   */
  public truncateMinutes(date: Date): any {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), date.getHours(), 0, 0, 0);
  }

  /**
   * Creates a new date down to the date component. Hourse,  Minutes, Seconds, Milleseconds are set to 0
   * @param date
   * @returns
   */
  public truncateHours(date: Date): any {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
  }

  public nextSunday(d: Date) {
    return nextSunday(d);
  }
  public nextSaturday(d: Date) {
    return nextSaturday(d);
  }

  /**
   * Creates a new date and sets the hour and minutes on the new date
   * @param date
   * @param hour
   * @param minute
   * @returns
   */
  public cloneDateSetTime(date: Date, hour: number, minute: number): Date {
    return new Date(date.getFullYear(), date.getMonth(), date.getDate(), hour, minute);
  }

  /**
   * Is the day of the week a Sunday?
   * @param d
   * @returns
   */
  public isSunday(d: Date) {
    // An integer number, between 0 and 6, corresponding to the day of the week for the given date, according to local time: 0 for Sunday, 1 for Monday, 2 for Tuesday, and so on.
    return d.getDay() === 0;
  }

  /**
   * Gets the UTC Timezone Offset in Minutes
   * @returns
   */
  public getTimezoneOffsetMinutes() {
    return new Date().getTimezoneOffset();
  }

  /**
   * Gets the next date for the month/day
   * @param month
   * @param day
   * @returns
   */
  public nextDate(month: number, day: number) {
    var d = new Date(this.currentYear, month, day);

    if (this.isPast(d)) {
      d = new Date(this.currentYear + 1, month, day);
    }

    return d;
  }

  /**
   * Test 2 date ranges to see if they overlap
   * @param dateRange1
   * @param dateRange2
   * @returns
   */
  public dateRangeOverlap(dateRange1: DateRange, dateRange2: DateRange) {
    return this.overlaps(dateRange1.start, dateRange1.end, dateRange2.start, dateRange2.end);
  }

  /**
   * Compares the start/end of 2 date ranges to see if they overlap
   * https://stackoverflow.com/a/22785208/386619
   * @param a_start
   * @param a_end
   * @param b_start
   * @param b_end
   * @returns
   */
  public overlaps(a_start: Date, a_end: Date, b_start: Date, b_end: Date) {
    if (a_start <= b_start && b_start <= a_end) return true; // b starts in a
    if (a_start <= b_end && b_end <= a_end) return true; // b ends in a
    if (b_start < a_start && a_end < b_end) return true; // a in b
    return false;
  }

  /**
   * Compares start/end dates to make sure they are contained within another date range/time window
   * @param child_start
   * @param child_end
   * @param parent_start
   * @param parent_end
   */
  public isWithin(child_start: Date, child_end: Date, parent_start: Date, parent_end: Date) {
    if (child_start < parent_start || child_start > parent_end || child_end < parent_start || child_end > parent_end) {
      return false;
    }
    return true;
  }

  /**
   * Get Month Names
   */
  public get monthNames() : string[] {
    return this._monthNames;
  }
}
